import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Avatar, Upload, Tooltip, Typography, Alert, Divider, Space } from 'antd';
import { UserOutlined, CameraOutlined } from '@ant-design/icons';
import { uploadAvatar } from '../apiClient';

const { Text } = Typography;

export interface User {
  username: string;
  email: string;
  avatarUrl?: string;
  userGroup?: string;
}

export interface AccountSettingsProps {
  /** Current user object */
  user: User | null;
  /** Function to update user profile - returns Promise of any type (will be ignored) */
  updateProfile: (data: { username: string; avatarUrl?: string }) => Promise<unknown>;
  /** Function to refresh user profile after update */
  refreshProfile: () => Promise<void>;
  /** Whether to show userGroup field (for Electron) */
  showUserGroup?: boolean;
  /** Layout variant */
  variant?: 'compact' | 'full';
}

/**
 * Shared AccountSettings component for user profile management
 * Used in both Electron and Web SettingsModal
 */
export const AccountSettings: React.FC<AccountSettingsProps> = ({
  user,
  updateProfile,
  refreshProfile,
  showUserGroup = false,
  variant = 'full',
}) => {
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(user?.avatarUrl);
  const [pendingAvatarData, setPendingAvatarData] = useState<string | null>(null); // 待上传的 base64 数据
  const [hasChanges, setHasChanges] = useState(false);
  const [initialUsername, setInitialUsername] = useState<string>('');

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        username: user.username,
        email: user.email,
      });
      setAvatarUrl(user.avatarUrl);
      setInitialUsername(user.username);
      setPendingAvatarData(null); // 重置待上传数据
    }
  }, [user, form]);

  // 监听表单变化
  const handleFormChange = () => {
    const currentUsername = form.getFieldValue('username');
    const hasUsernameChanged = currentUsername !== initialUsername;
    const hasAvatarChanged = pendingAvatarData !== null; // 有待上传的头像
    setHasChanges(hasUsernameChanged || hasAvatarChanged);
  };

  const handleSaveProfile = async () => {
    try {
      const values = await form.validateFields();
      setIsSaving(true);

      let finalAvatarUrl = avatarUrl;

      // 如果有待上传的头像，先上传到 R2
      if (pendingAvatarData) {
        try {
          const uploadResult = await uploadAvatar(pendingAvatarData);
          finalAvatarUrl = uploadResult.avatarUrl;
          console.log('[AccountSettings] Avatar uploaded to R2:', finalAvatarUrl);
        } catch (uploadError: any) {
          console.error('[AccountSettings] Failed to upload avatar:', uploadError);
          message.error('头像上传失败：' + (uploadError?.message || '未知错误'));
          setIsSaving(false);
          return; // 上传失败则终止
        }
      }

      await updateProfile({
        username: values.username,
        avatarUrl: finalAvatarUrl,
      });

      await refreshProfile();

      message.success('个人信息已更新');
      setInitialUsername(values.username);
      setPendingAvatarData(null); // 清空待上传数据
      setHasChanges(false);
    } catch (error: any) {
      console.error('更新失败:', error);
      message.error(error?.message || '更新失败');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarUpload = async (file: File) => {
    // 验证文件类型
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return false;
    }

    // 验证文件大小 (5MB)
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过 5MB！');
      return false;
    }

    // 读取文件并转换为 base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setAvatarUrl(dataUrl); // 立即显示预览
      setPendingAvatarData(dataUrl); // 保存待上传数据
      setHasChanges(true); // 直接标记有变化（因为 setState 是异步的）
      message.success('头像已选择，请点击保存按钮');
    };
    reader.readAsDataURL(file);

    return false; // 阻止默认上传行为
  };

  if (!user) {
    return (
      <Alert
        title="未登录"
        description="请先登录以管理账户信息"
        type="warning"
        showIcon
      />
    );
  }

  // Compact variant (for web)
  if (variant === 'compact') {
    return (
      <div style={{ maxWidth: 400 }}>
        {/* 头像编辑 */}
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
          <Upload
            showUploadList={false}
            beforeUpload={handleAvatarUpload}
            accept="image/*"
          >
            <div style={{ position: 'relative', cursor: 'pointer' }}>
              <Avatar
                src={avatarUrl}
                size={64}
                icon={<UserOutlined />}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: 'var(--ant-color-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CameraOutlined style={{ fontSize: 12, color: '#fff' }} />
              </div>
            </div>
          </Upload>
          <div>
            <Text strong>头像</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>点击更换头像</Text>
          </div>
        </div>

        {/* 表单 */}
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleFormChange}
        >
          <Form.Item
            label="用户名"
            name="username"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item
            label="邮箱"
            name="email"
          >
            <Input disabled />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              onClick={handleSaveProfile}
              loading={isSaving}
              disabled={!hasChanges}
            >
              保存更改
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  }

  // Full variant (for electron)
  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        
        <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 12 }}>
          个人信息
        </Text>

        {/* 头像区域 */}
        <div style={{ marginBottom: 24 }}>
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={handleAvatarUpload}
          >
            <Tooltip title="点击上传头像">
              <div style={{ position: 'relative', cursor: 'pointer', width: 56, height: 56 }}>
                <Avatar 
                  size={56} 
                  icon={<UserOutlined />}
                  src={avatarUrl}
                  style={{ 
                    border: '2px solid var(--ant-color-border)',
                    transition: 'all 0.3s'
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: 'rgba(0, 0, 0, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                >
                  <CameraOutlined style={{ color: '#fff', fontSize: 20 }} />
                </div>
              </div>
            </Tooltip>
          </Upload>
        </div>

        {/* 用户名编辑区域 */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            onValuesChange={handleFormChange}
            style={{ marginBottom: 0 }}
          >
            <Form.Item
              name="username"
              label="用户名"
              rules={[
                { required: true, message: '请输入用户名' },
                { min: 2, message: '用户名至少 2 个字符' },
                { max: 32, message: '用户名最多 32 个字符' },
              ]}
              style={{ marginBottom: 0 }}
            >
              <Input 
                placeholder="用户名" 
                style={{ width: 180 }}
              />
            </Form.Item>
          </Form>

          <Button 
            type="primary"
            disabled={!hasChanges}
            loading={isSaving}
            onClick={handleSaveProfile}
            style={{ marginTop: 30 }}
          >
            保存变更
          </Button>
        </div>


      <Divider style={{ margin: '12px 0' }} />


      {/* 账户信息与安全 section */}
      <div style={{ paddingTop: 9 }}>
        <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 12 }}>
          账户信息与安全
        </Text>
      
        <Space orientation="vertical" size="small" style={{ width: '100%', gap: 3, marginBottom: 8 }}>
          <div style={{ padding: 12, background: 'var(--ant-color-bg-container-disabled)', borderRadius: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text>用户组</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {user.userGroup || 'FREE'}
                </Text>
              </div>
              <Tooltip title="该功能将在日后更新">
                <Button
                  type="text"
                  disabled
                >
                  升级或更改
                </Button>
              </Tooltip>
            </div>
          </div>

          <div style={{ padding: 12, background: 'var(--ant-color-bg-container-disabled)', borderRadius: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text>邮箱</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {user.email}
                </Text>
                <br />
                {/* <Text type="secondary" style={{ fontSize: 12 }}>
                修改邮箱功能将在日后推出
                </Text> */}
              </div>
              <Tooltip title="该功能将在日后更新">
                <Button
                  type="text"
                  disabled
                >
                  更改邮箱
                </Button>
              </Tooltip>
            </div>
          </div>

          <div style={{ padding: 12, background: 'var(--ant-color-bg-container-disabled)', borderRadius: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text>密码</Text>
                {/* <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  用于登录的密码
                </Text> */}
              </div>
              <Tooltip title="该功能将在日后更新">
                <Button
                  type="text"
                  disabled
                >
                  更改密码
                </Button>
              </Tooltip>
            </div>
          </div>

        </Space>
      </div>

      </div>
    </div>
  );
};
